import {isAgent, isBroker, isSeller, isTC} from '@helpers/user';
import {
    appUrlOrPathnameByUserType,
    isModuleOfType,
    isOnMainDomain,
    matchCommonGuestRoute,
    matchCommonIntegrationsRoute,
    toggleSSPALoader,
    updateLocationTo,
} from "@helpers/router-helper";
import {checkLoggedSession} from "@helpers/api-helper";
import {debugf} from "@helpers/string";
import {createPinia, setActivePinia} from 'pinia';
import {useAuthStore} from '@store';
import piniaPersist from 'pinia-plugin-persist';

export const boot = await (async () => {
    //suppress some annoying errors that we can't fix until vue3 related to computed values ~does not change the way the app works
    let oldClogError = console.error;
    console.error = function() {};

    const pinia = createPinia();
    //make our data persistent to a type of store
    pinia.use(piniaPersist);
    setActivePinia(pinia);

    const storeAuth = useAuthStore();

    console.error = oldClogError;

    let firstAppLoad = true,
        bag = {
            integrations: {
                load: async () => await import('integrations/app'),
                check: (l) => checkIfModuleIntegrations(l),
                mountable: true,
                disabled: false,
            },
            guest: {
                load: async () => await import('guest/app'),
                check: (l) => checkIfModuleGuest(l),
                mountable: true,
                disabled: false,
            },
            seller: {
                load: async () => await import('seller/app'),
                check: checkIfModuleSeller,
                mountable: true,
                disabled: false,
            },
            agent: {
                load: async () => await import('agent/app'),
                check: checkIfModuleAgent,
                mountable: true,
                disabled: false,
            },
            broker: {
                load: async () => await import('broker/app'),
                check: checkIfModuleBroker,
                mountable: true,
                disabled: false,
            },
            transactioncoordinator: {
                load: async () => await import('transactioncoordinator/app'),
                check: checkIfModuleTransactionCoordinator,
                mountable: true,
                disabled: false,
            },
        },
        loadApp = async function (app = null) {
            //ugly but simple for now
            toggleSSPALoader('on');

            if (app) {
                try {
                    if (!Boolean(bag[app])) {
                        throw "Unknown app.\n\n";
                    }

                    // debugf(`checkIf-- Loading module ${app}/app.`);
                    let module = await bag[app].load();

                    if (module) {
                        setTimeout(() => firstAppLoad = false, 50);
                        if (!['integrations'].includes(app)) {
                            //skip mounting only one time for integrations when it goes off
                            setTimeout(() => bag['integrations'].mountable = true, 2500);
                            //hide loader execept for integrations app
                            toggleSSPALoader('off');
                        }
                        setActivePinia(pinia);
                        return module;
                    }
                } catch (e) {
                    //fallback | treat app loading errors
                    debugf(`checkIf-- Unable to load module ${app}/app.`);
                    console.error(e);
                }
            }
        };

    function isMountable(userTypeAppName) {
        return Boolean(bag[userTypeAppName]?.mountable) && !Boolean(bag[userTypeAppName]?.disabled);
    }

    function isOnGuestRouteForAll(location) {
        return matchCommonGuestRoute(location.pathname) && (
            location.href.indexOf('/logout') === -1 ||
            location.href.indexOf(window.env.APP_URL + '/logout') !== -1
        );
    }

    function checkIfModuleIntegrations(location, debug = true) {
        //this one will be toggled on/off by it's own code mostly
        let result = isMountable('integrations') && !isOnGuestRouteForAll(location) && (
            firstAppLoad || matchCommonIntegrationsRoute(location)
        );


        // debug && debugf([`3.checkIf--integrations`, result]);

        return result;
    }

    function checkIfModule(location, userTypeAppName, checkForCallback = null) {
        let mountable = isMountable(userTypeAppName),
            guestRouteForAll = mountable ? isOnGuestRouteForAll(location) : null,
            bootIntegrations = mountable && !guestRouteForAll ? checkIfModuleIntegrations(location, false) : null;

        if (!mountable || guestRouteForAll || bootIntegrations) {
            // debugf([`1.checkIf--${userTypeAppName}`, false], {mountable, guestRouteForAll, bootIntegrations});
            return false;
        }

        let moduleHostOrType = appUrlOrPathnameByUserType(userTypeAppName),
            useHostCheck = Boolean(moduleHostOrType) && moduleHostOrType[0] !== '/',
            isAppUrlUp2Date = location.pathname.indexOf('/webhook') > -1 || isModuleOfType(location, moduleHostOrType, useHostCheck),
            me = storeAuth.me,
            result = isAppUrlUp2Date;

        if ((!result || me) && (!checkForCallback || checkForCallback(me))) {

            let redirect2Dashboard = !isOnMainDomain() && [
                (useHostCheck ? '' : window.env.APP_URL) + moduleHostOrType,
                (useHostCheck ? '' : window.env.APP_URL) + moduleHostOrType + '/auth',
            ].includes(location.href);

            if (redirect2Dashboard) {
                moduleHostOrType += '/dashboard';
            }

            const urlParams = new URLSearchParams(location.search);

            result = isAppUrlUp2Date || location.href.indexOf('/logout') > -1;

            //don't redirect on logout or if we have the correct subdomain
            result || updateLocationTo(
                moduleHostOrType, useHostCheck, Boolean(urlParams.get('forceRedirect')) ? 'force' : true
            );
        }

        // debugf([`2.checkIf--${userTypeAppName}`, result]);

        return result;
    }

    function checkIfModuleSeller(location) {
        return checkIfModule(
            location, 'seller', me => me && isSeller(me)
        );
    }

    function checkIfModuleAgent(location) {
        return checkIfModule(
            location, 'agent', me => me && isAgent(me)
        );
    }

    function checkIfModuleBroker(location) {
        return checkIfModule(
            location, 'broker', me => me && isBroker(me)
        );
    }

    function checkIfModuleTransactionCoordinator(location) {
        return checkIfModule(
            location, 'transactioncoordinator', me => me && isTC(me)
        );
    }
    function checkIfModuleGuest(location) {
        let mountable = isMountable('guest'),
            guestRouteForAll = isOnGuestRouteForAll(location),
            bootIntegrations = mountable && !guestRouteForAll ? checkIfModuleIntegrations(location, false) : null,
            result = mountable && (guestRouteForAll || !(
                bootIntegrations ||
                checkIfModuleSeller(location) ||
                checkIfModuleAgent(location) ||
                checkIfModuleBroker(location) ||
                checkIfModuleTransactionCoordinator(location)
            ));

        // debugf(['4.checkIf--guest', result], {mountable, guestRouteForAll, bootIntegrations});
        return result
    }

    toggleSSPALoader('on');

    if (Boolean(window.SSPA)) {
        // debugf("Initializing orchestrator.");

        //logout from start if we detect that we need to // avoids problems with ss unified login bugs
        await checkLoggedSession(
            window.location.pathname.indexOf('/logout') > -1 ||
            matchCommonIntegrationsRoute() && window.location.search.indexOf('accessToken=') > -1,
            false
        );

        window.SSPA.bag = bag;
        window.SSPA.loadApp = loadApp;

        return {pinia};
    }

    return false;
})(this);
